import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BusyIndicatorMessageManager, BusyIndicatorModule } from '../../../../../app/Busy-Indicator';
import { TaskProcessingActions } from '../../../../Compliance/Return/Models/enums';
import { IWeissmanModalComponent } from '../../../../Compliance/WeissmanModalService';
import { ModalWrapperComponent } from '../../../../UI-Lib/Modal-Wrapper/modalWrapper.component';
import { ReferralsService } from '../../referrals.service';
import { UserTeamPickerComponent } from '../../../../Common/User-Pickers/user-team-picker.component';
import { UILibModule } from 'src/app/UI-Lib/UILib.module';
import { FormsModule } from '@angular/forms';

export interface ReferralTaskExceptionModalParams {
    workflowStatus: Core.LitigationReferralWorkflowStatusModel;
    litigationReferralId: number;
}

export interface ReferralTaskExceptionModalResult extends Core.LitigationReferralTaskUpdateResultModel {}

interface LRTaskExceptionOption {
    taskId: number;
    action: TaskProcessingActions;
    title: string;
    taskName: string;
    assignedUserId: System.Guid;
    assignedTeamId: number;
    isAuthorized?: boolean;
    message: string;
}

@Component({
    selector: 'referral-task-exception-modal',
    templateUrl: 'referralTaskExceptionModal.component.html',
    imports: [ModalWrapperComponent, UserTeamPickerComponent, BusyIndicatorModule, UILibModule, FormsModule],
})
export class ReferralTaskExceptionModalComponent
    implements OnInit, IWeissmanModalComponent<ReferralTaskExceptionModalParams, ReferralTaskExceptionModalResult>
{
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _referralsService: ReferralsService,
    ) {}

    params: ReferralTaskExceptionModalParams;
    result: ReferralTaskExceptionModalResult;

    busyIndicatorMessageManager = new BusyIndicatorMessageManager<string>();
    comment: string | undefined;
    options: LRTaskExceptionOption[] = [];
    selectedOption: LRTaskExceptionOption;
    TaskProcessingActions = TaskProcessingActions;

    ngOnInit() {
        this.options = [];
        if (this.params.workflowStatus.canReassignTask && this.params.workflowStatus.taskStatusName) {
            this.options.push({
                action: TaskProcessingActions.ReassignTask,
                taskId: this.params.workflowStatus.taskID,
                title: `Reassign ${this.params.workflowStatus.taskStatusName}`,
                message: 'Reassigning',
                taskName: this.params.workflowStatus.taskStatusName,
                assignedTeamId: 0,
                assignedUserId: '',
                isAuthorized: !!this.params.workflowStatus.taskStatusName,
            });
        }

        if (this.params.workflowStatus.canSkipTask && this.params.workflowStatus.taskAdvanceName) {
            this.options.push({
                action: TaskProcessingActions.SkipTask,
                taskId: this.params.workflowStatus.taskID,
                title: `Skip ${this.params.workflowStatus.taskAdvanceName}`,
                message: 'Skipping',
                taskName: this.params.workflowStatus.taskAdvanceName,
                assignedTeamId: 0,
                assignedUserId: '',
                isAuthorized: !!this.params.workflowStatus.taskStatusName,
            });
        }

        if (this.params.workflowStatus.canRescindTask && this.params.workflowStatus.taskRescindName) {
            this.options.push({
                action: TaskProcessingActions.RescindTask,
                taskId: this.params.workflowStatus.taskID,
                title: `Rescind ${this.params.workflowStatus.taskRescindName}`,
                message: 'Rescinding',
                taskName: 'placeholder',
                assignedTeamId: 0,
                assignedUserId: '',
                isAuthorized: true,
            });
        }
    }

    selectedOptionChanged(e: any) {
        console.log(e);
    }

    userChanged(newUser: Core.UserTeamModel, option: LRTaskExceptionOption) {
        option.assignedTeamId = newUser.teamID;
        option.assignedUserId = newUser.userID;
    }

    async save() {
        const busyMsg = 'saving';
        this.busyIndicatorMessageManager.add(this.selectedOption.message, busyMsg);

        try {
            this.result = await this._actOnTask();

            this._bsModalRef.hide();
        } finally {
            this.busyIndicatorMessageManager.remove(busyMsg);
        }

        return Promise.resolve();
    }

    cancel() {
        this._bsModalRef.hide();
    }

    private async _actOnTask(): Promise<ReferralTaskExceptionModalResult> {
        const updateModel = {
            litigationReferralId: this.params.litigationReferralId,
            taskId: this.params.workflowStatus.taskID,
            comment: this.comment,
        };

        switch (this.selectedOption.action) {
            case TaskProcessingActions.ReassignTask: {
                const updateResultModel = await this._referralsService.reassignTask(this.params.litigationReferralId, {
                    ...updateModel,
                    assignedTeamId: this.selectedOption.assignedTeamId,
                    assignedUserId: this.selectedOption.assignedUserId,
                });

                return updateResultModel;
            }
            case TaskProcessingActions.SkipTask: {
                const updateResultModel = await this._referralsService.skipTask(
                    this.params.litigationReferralId,
                    updateModel,
                );
                return updateResultModel;
            }
            case TaskProcessingActions.RescindTask: {
                const updateResultModel = await this._referralsService.rescindTask(
                    this.params.litigationReferralId,
                    updateModel,
                );
                return updateResultModel;
            }
        }
    }
}
